import type { Question } from '@submitpro/shared';
import { ConversationEngine, type EngineMode } from '@submitpro/shared';
import { useCallback, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// useConversationEngine — React hook wrapping the ConversationEngine
// ---------------------------------------------------------------------------

export interface ConversationEngineState {
  /** The current question to display, or null if complete */
  currentQuestion: Question | null;
  /** Answer the current question and advance */
  answer: (value: unknown) => void;
  /** Go back to the previous question */
  back: () => void;
  /** Current progress info */
  progress: {
    percentage: number;
    answered: number;
    total: number;
    bySection: Record<string, { answered: number; total: number; percentage: number }>;
  };
  /** Whether the flow is complete (no more questions) */
  isComplete: boolean;
  /** Nested submission data built from answers + field_paths */
  submissionData: Record<string, unknown>;
  /** All currently visible questions */
  activeQuestions: Question[];
  /** Flat answers map keyed by question id */
  answers: Record<string, unknown>;
  /** Skip the current question (only works for optional) */
  skip: () => void;
  /** Load saved answers into the engine (e.g. from a draft) */
  loadAnswers: (answers: Record<string, unknown>) => void;
  /** Merge partial answers into the engine and re-evaluate (e.g. AI prefill) */
  prefillAnswers: (partial: Record<string, unknown>) => void;
  /** The engine mode */
  mode: EngineMode;
}

/**
 * React hook that wraps ConversationEngine for use in intake forms.
 *
 * @param questions - The full question set to load into the engine
 * @param mode - 'prospect' for public-facing flow, 'agent' for internal use
 */
export function useConversationEngine(
  questions: Question[],
  mode: EngineMode = 'prospect',
): ConversationEngineState {
  // Engine is created once and mutated; we use a ref so it persists across renders
  const engineRef = useRef<ConversationEngine | null>(null);

  // Initialize engine if not yet created or if questions/mode changed
  if (!engineRef.current) {
    engineRef.current = new ConversationEngine(questions, mode);
  }

  const engine = engineRef.current;

  // Trigger re-renders when engine state changes
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const currentQuestion = engine.getCurrentQuestion();
  const progress = engine.getProgress();
  const isComplete = currentQuestion === null;
  const activeQuestions = engine.getActiveQuestions();
  const submissionData = engine.getSubmissionData();
  const answers = engine.getAnswers();

  const answer = useCallback(
    (value: unknown) => {
      const q = engine.getCurrentQuestion();
      if (!q) return;
      engine.answer(q.id, value);
      bump();
    },
    [engine, bump],
  );

  const back = useCallback(() => {
    engine.back();
    bump();
  }, [engine, bump]);

  const skip = useCallback(() => {
    const q = engine.getCurrentQuestion();
    if (!q || q.required) return;
    // Move to next without storing an answer
    engine.answer(q.id, undefined);
    bump();
  }, [engine, bump]);

  const loadAnswers = useCallback(
    (savedAnswers: Record<string, unknown>) => {
      engine.loadAnswers(savedAnswers);
      bump();
    },
    [engine, bump],
  );

  const prefillAnswers = useCallback(
    (partial: Record<string, unknown>) => {
      engine.loadAnswers({ ...engine.getAnswers(), ...partial });
      bump();
    },
    [engine, bump],
  );

  return useMemo(
    () => ({
      currentQuestion,
      answer,
      back,
      progress,
      isComplete,
      submissionData,
      activeQuestions,
      answers,
      skip,
      loadAnswers,
      prefillAnswers,
      mode,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bump version drives re-computation
    [
      currentQuestion,
      answer,
      back,
      progress,
      isComplete,
      submissionData,
      activeQuestions,
      answers,
      skip,
      loadAnswers,
      prefillAnswers,
      mode,
    ],
  );
}
